import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../services/AuthContext';
import { useToast } from '../services/ToastContext';
import api from '../services/api';
import Navbar from '../components/Navbar';
import PostCard from '../components/PostCard';
import CreatePost from '../components/CreatePost';
import '../styles/Feed.css';

function SkeletonPost() {
  return (
    <div className="post-card skeleton-post" aria-hidden="true">
      <div className="skeleton-post-header">
        <div className="skeleton skeleton-avatar-circle"></div>
        <div className="skeleton-post-meta">
          <div className="skeleton skeleton-line" style={{ width: '130px' }}></div>
          <div className="skeleton skeleton-line" style={{ width: '72px', marginTop: '6px' }}></div>
        </div>
      </div>
      <div className="skeleton skeleton-line" style={{ width: '100%', marginTop: '14px' }}></div>
      <div className="skeleton skeleton-line" style={{ width: '88%', marginTop: '8px' }}></div>
      <div className="skeleton skeleton-line" style={{ width: '55%', marginTop: '8px' }}></div>
    </div>
  );
}

function OnboardingCard({ navigate }) {
  return (
    <div className="onboarding-card">
      <div className="onboarding-intro">
        <h2>Welcome to <span className="onboarding-brand-accent">ppl</span>YouKnow</h2>
        <p>Your private feed is ready. Here's how to get started:</p>
      </div>
      <div className="onboarding-steps">
        <button className="onboarding-step" onClick={() => navigate('/profile')}>
          <span className="onboarding-step-num">1</span>
          <div className="onboarding-step-body">
            <strong>Set up your profile</strong>
            <span>Add a photo so friends recognize you</span>
          </div>
          <span className="onboarding-step-cta">→</span>
        </button>
        <button className="onboarding-step" onClick={() => navigate('/friends')}>
          <span className="onboarding-step-num">2</span>
          <div className="onboarding-step-body">
            <strong>Add your friends</strong>
            <span>Invite by email or phone number</span>
          </div>
          <span className="onboarding-step-cta">→</span>
        </button>
        <div className="onboarding-step onboarding-step-passive">
          <span className="onboarding-step-num">3</span>
          <div className="onboarding-step-body">
            <strong>Share something</strong>
            <span>Use the box above to post to your friends</span>
          </div>
          <span className="onboarding-step-cta onboarding-up-arrow">↑</span>
        </div>
      </div>
    </div>
  );
}

function Feed() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const { user } = useAuth();
  const navigate = useNavigate();
  const { addToast } = useToast();

  useEffect(() => {
    fetchPosts();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchPosts = async () => {
    try {
      setLoading(true);
      const response = await api.get('/posts/');
      setPosts(response.data.posts);
    } catch {
      setError('Failed to load posts. Please refresh.');
    } finally {
      setLoading(false);
    }
  };

  const handlePostCreated = (newPost) => {
    setPosts(prev => [newPost, ...prev]);
  };

  const handleDeletePost = async (postId) => {
    try {
      await api.delete(`/posts/${postId}`);
      setPosts(prev => prev.filter(p => p.id !== postId));
      addToast('Post deleted', 'info');
    } catch {
      addToast('Failed to delete post', 'error');
    }
  };

  return (
    <div className="page-container">
      <Navbar />

      <div className="feed-container">
        <div className="feed-content">
          <CreatePost onPostCreated={handlePostCreated} />

          {loading ? (
            <div className="posts-list">
              <SkeletonPost />
              <SkeletonPost />
              <SkeletonPost />
            </div>
          ) : error ? (
            <div className="error-message">{error}</div>
          ) : posts.length === 0 ? (
            <OnboardingCard navigate={navigate} />
          ) : (
            <div className="posts-list">
              {posts.map(post => (
                <PostCard
                  key={post.id}
                  post={post}
                  currentUserId={user?.id}
                  onDelete={handleDeletePost}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Feed;
